import { Observable } from 'rxjs'
import { concatAll, filter, ignoreElements, switchMap, tap } from 'rxjs/operators'
import { combineEpics, ofType, StateObservable } from 'redux-observable'
import { GlobalState } from 'store'
import { ActionsUnion, createAction, isOpenFin, openFinPromisify } from 'utils'
import { SelectionActions, SelectionActionTypes } from 'store/selection'
import { createWindowSelectionsSelector } from 'selectors/selectionSelectors'
import { WindowService } from 'services'
import { EntityType } from 'api'
import { Locale, Translation } from 'locale'
import { RepositoryActions } from 'store/repository'
import { ConnectionDependencies } from './connectionEpic'
import { BTQAction } from 'epics'

export interface WindowDependencies {
  windowService: WindowService
}

export enum WindowActionTypes {
  TEAROUT_GRID = 'TEAROUT_GRID',
  OPEN_TICKET_WINDOW = 'OPEN_TICKET_WINDOW',
  OPEN_ASSIGNMENT_DIALOG = 'OPEN_ASSIGNMENT_DIALOG',
  OPEN_ALLOCATION_DIALOG = 'OPEN_ALLOCATION_DIALOG',
  NAVIGATE_TO = 'NAVIGATE_TO'
}

export const WindowActions = {
  tearoutGrid: (url: string) => createAction(WindowActionTypes.TEAROUT_GRID, { url }),
  openTicketWindow: (entityId: string, entityType: EntityType, windowTitle: string, ownOrder?: boolean, history?: boolean) =>
    createAction(WindowActionTypes.OPEN_TICKET_WINDOW, { entityId, entityType, windowTitle, history }, { ownOrder }),
  openAssignmentDialog: (dealId: string, windowTitle: string) =>
    createAction(WindowActionTypes.OPEN_ASSIGNMENT_DIALOG, { dealId, windowTitle }),
  openAllocationDialog: (dealId: string, windowTitle: string) =>
    createAction(WindowActionTypes.OPEN_ALLOCATION_DIALOG, { dealId, windowTitle }),
  navigateTo: (path: string) => createAction(WindowActionTypes.NAVIGATE_TO, { path })
}

export type WindowActions = ActionsUnion<typeof WindowActions>

type TearoutGridAction = ReturnType<typeof WindowActions.tearoutGrid>

export function tearoutGridEpic(
  action$: Observable<BTQAction>,
  state$: StateObservable<GlobalState>,
  dependencies: WindowDependencies
): Observable<BTQAction> {
  const windowSelectionsSelector = createWindowSelectionsSelector()

  return action$.pipe(
    ofType<BTQAction, WindowActionTypes.TEAROUT_GRID, TearoutGridAction>(WindowActionTypes.TEAROUT_GRID),
    tap((action: TearoutGridAction) => {
      const { url } = action.payload
      const windowSelections = windowSelectionsSelector(state$.value)

      const customData = {
        windowSelections: {
          selectedGridSettings: windowSelections.selectedGridSettings,
          organisationId: windowSelections.organisationId,
          selectedCollectionIds: windowSelections.selectedCollectionIds
        }
      }

      dependencies.windowService.createWindow({
        url,
        defaultWidth: 1200,
        defaultHeight: 800,
        accelerator: { zoom: true },
        customData
      })
    }),
    ignoreElements()
  )
}

type OpenActivityMonitorAction = ReturnType<typeof SelectionActions.openActivityMonitor>

export function openActivityMonitorEpic(
  action$: Observable<BTQAction>,
  _state$: StateObservable<GlobalState>,
  dependencies: WindowDependencies
): Observable<BTQAction> {
  return action$.pipe(
    ofType<BTQAction, SelectionActionTypes.OPEN_ACTIVITY_MONITOR, OpenActivityMonitorAction>(SelectionActionTypes.OPEN_ACTIVITY_MONITOR),
    filter(action => action.payload.activityMonitorOpen && !!isOpenFin()),
    switchMap(async () => {
      const existing = await dependencies.windowService.findWindow('activityMonitor')

      if (existing) {
        const bringToFrontAsync = openFinPromisify(existing.bringToFront, existing)
        await bringToFrontAsync()
        return []
      } else {
        const url = '/tearout/activitymonitor'
        const customData = {
          windowTitle: Locale.translate(Translation.TC_ACTIVITY_MONITOR)
        }
        const win = await dependencies.windowService.createWindow({
          name: 'activityMonitor',
          url,
          minWidth: 320,
          maxWidth: 320,
          minHeight: 400,
          defaultHeight: 450,
          maximizable: false,
          customData
        })
        await new Promise(resolve => win.addEventListener('closed', resolve))
        return [SelectionActions.openActivityMonitor(false)]
      }
    }),
    concatAll()
  )
}

type OpenTicketWindowAction = ReturnType<typeof WindowActions.openTicketWindow>

const getOpenTicketUrl = (entityType: EntityType, entityId: string, ownOrder?: boolean, history?: boolean) => {
  switch (entityType) {
    case EntityType.NEGOTIATION:
      return history
        ? `/tearout/rfqs/${entityId}/historyTicket`
        : ownOrder
        ? `/tearout/rfqs/${entityId}/initiatorTicket`
        : `/tearout/rfqs/${entityId}/responderTicket`
    case EntityType.DEAL:
      return `/tearout/trades/${entityId}/ticket`
    default: {
      throw new Error(`Unsupported entity type for open window: ${entityType}`)
    }
  }
}

export function openTicketWindowEpic(
  action$: Observable<BTQAction>,
  _state$: StateObservable<GlobalState>,
  dependencies: WindowDependencies
): Observable<BTQAction> {
  return action$.pipe(
    ofType<BTQAction, WindowActionTypes.OPEN_TICKET_WINDOW, OpenTicketWindowAction>(WindowActionTypes.OPEN_TICKET_WINDOW),
    switchMap(async action => {
      const { entityId, entityType, history } = action.payload
      const prefix = `${entityType.toLocaleLowerCase()}Ticket`
      const name = `${prefix}-${entityId}`

      const existing = await dependencies.windowService.findWindow(name)

      if (existing) {
        const bringToFrontAsync = openFinPromisify(existing.bringToFront, existing)
        await bringToFrontAsync()
        return []
      } else {
        const url = getOpenTicketUrl(entityType, entityId, action.meta.ownOrder, history)
        const customData = {
          windowTitle: action.payload.windowTitle
        }
        const win = await dependencies.windowService.createWindow({
          name,
          url,
          minWidth: 480,
          defaultWidth: 480,
          minHeight: 576,
          defaultHeight: 576,
          maximizable: false,
          customData
        })
        await new Promise(resolve => win.addEventListener('closed', resolve))
        return [RepositoryActions.revertUpdates(entityType, entityId)]
      }
    }),
    concatAll()
  )
}

type OpenAssignmentDialogAction = ReturnType<typeof WindowActions.openAssignmentDialog>

export function openAssignmentDialogEpic(
  action$: Observable<BTQAction>,
  _state$: StateObservable<GlobalState>,
  dependencies: WindowDependencies
): Observable<BTQAction> {
  return action$.pipe(
    ofType<BTQAction, WindowActionTypes.OPEN_ASSIGNMENT_DIALOG, OpenAssignmentDialogAction>(WindowActionTypes.OPEN_ASSIGNMENT_DIALOG),
    tap(async (action: OpenAssignmentDialogAction) => {
      const { dealId, windowTitle } = action.payload
      const name = `assignmentDialog-${dealId}`
      const existing = await dependencies.windowService.findWindow(name)

      if (existing) {
        const bringToFrontAsync = openFinPromisify(existing.bringToFront, existing)
        await bringToFrontAsync()
      } else {
        await dependencies.windowService.createWindow({
          name,
          url: `/tearout/assignmentDialog/${dealId}`,
          minWidth: 1248,
          defaultWidth: 1248,
          minHeight: 640,
          defaultHeight: 640,
          maximizable: false,
          customData: {
            windowTitle
          }
        })
      }
    }),
    ignoreElements()
  )
}

type OpenAllocationDialogAction = ReturnType<typeof WindowActions.openAllocationDialog>

export function openAllocationDialogEpic(
  action$: Observable<BTQAction>,
  _state$: StateObservable<GlobalState>,
  dependencies: WindowDependencies
): Observable<BTQAction> {
  return action$.pipe(
    ofType<BTQAction, WindowActionTypes.OPEN_ALLOCATION_DIALOG, OpenAllocationDialogAction>(WindowActionTypes.OPEN_ALLOCATION_DIALOG),
    tap(async (action: OpenAllocationDialogAction) => {
      const { dealId, windowTitle } = action.payload
      const name = `allocationDialog-${dealId}`
      const existing = await dependencies.windowService.findWindow(name)

      if (existing) {
        const bringToFrontAsync = openFinPromisify(existing.bringToFront, existing)
        await bringToFrontAsync()
      } else {
        await dependencies.windowService.createWindow({
          name,
          url: `/tearout/allocationDialog/${dealId}`,
          minWidth: 1248,
          defaultWidth: 1248,
          minHeight: 680,
          defaultHeight: 680,
          maximizable: false,
          customData: {
            windowTitle
          }
        })
      }
    }),
    ignoreElements()
  )
}

type NavigateToAction = ReturnType<typeof WindowActions.navigateTo>

export function navigateToEpic(
  action$: Observable<BTQAction>,
  _state$: StateObservable<GlobalState>,
  dependencies: WindowDependencies & ConnectionDependencies
): Observable<BTQAction> {
  return action$.pipe(
    ofType<BTQAction, WindowActionTypes.NAVIGATE_TO, NavigateToAction>(WindowActionTypes.NAVIGATE_TO),
    tap(async (action: NavigateToAction) => {
      const { path } = action.payload
      dependencies.routerService.navigate(path)
      const application = await fin.desktop.Application.getCurrent()
      application.getWindow().restore()
      application.getWindow().bringToFront()
    }),
    ignoreElements()
  )
}

export const windowEpic = combineEpics(
  tearoutGridEpic,
  openActivityMonitorEpic,
  openTicketWindowEpic,
  openAssignmentDialogEpic,
  openAllocationDialogEpic,
  navigateToEpic
)
